import { queryClient } from '@gen2/config/queryClient';
import { useSafeQuery } from '@gen2/hooks';
import { useMutation, UseQueryOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { has } from 'lodash';
import { TInviteRequestGroup } from '../invite-requests/api';
import {
  createTemplate,
  createTemplateRequest,
  deleteTemplateRequest,
  deleteTemplates,
  duplicateTemplate,
  getTemplate,
  getTemplateRequestGroups,
  getTemplateRequests,
  getTemplates,
  renameTemplate,
  saveTemplate,
  selectTemplate,
  TGetTemplatesRequest,
  TGetTemplatesResponse,
  updateTemplate,
  updateTemplateRequestGroupName,
  updateTemplateRequest,
  duplicateTemplateGroupRequest,
} from './api';

export enum TemplateKeys {
  saveTemplate = 'saveTemplate',
  getTemplates = 'getTemplates',
  selectTemplate = 'selectTemplate',
  renameTemplate = 'renameTemplate',
  deleteTemplates = 'deleteTemplates',
  duplicateTemplate = 'duplicateTemplate',
  createTemplate = 'createTemplate',
  updateTemplate = 'updateTemplate',
  getTemplate = 'getTemplate',
  getTemplateRequests = 'getTemplateRequests',
  createTemplateRequest = 'createTemplateRequest',
  updateTemplateRequest = 'updateTemplateRequest',
  deleteTemplateRequest = 'deleteTemplateRequest',
  getTemplateRequestGroups = 'getTemplateRequestGroups',
}

export const useSelectTemplateMutation = () =>
  useMutation({
    mutationFn: selectTemplate,
    mutationKey: [TemplateKeys.selectTemplate],
  });

export const useSaveTemplateMutation = () =>
  useMutation({
    mutationFn: saveTemplate,
    mutationKey: [TemplateKeys.saveTemplate],
  });

export const useTemplatesQuery = (
  params?: TGetTemplatesRequest,
  options?: UseQueryOptions<TGetTemplatesResponse['data']>,
) =>
  useSafeQuery(
    [TemplateKeys.getTemplates, ...(Object.values(params ?? {}) as [])],
    async (): Promise<TGetTemplatesResponse['data']> => {
      const res = await getTemplates(params);
      return res?.data?.data;
    },
    {
      ...options,
    },
  );

export const useRenameTemplateMutation = () =>
  useMutation({
    mutationFn: renameTemplate,
    mutationKey: [TemplateKeys.renameTemplate],
  });

export const useDeleteTemplatesMutation = () =>
  useMutation({
    mutationFn: deleteTemplates,
    mutationKey: [TemplateKeys.deleteTemplates],
  });

export const useDuplicateTemplateMutation = () =>
  useMutation({
    mutationFn: duplicateTemplate,
    mutationKey: [TemplateKeys.duplicateTemplate],
  });

export const useCreateTemplateMutation = () =>
  useMutation({
    mutationFn: createTemplate,
    mutationKey: [TemplateKeys.createTemplate],
  });

export const useUpdateTemplateMutation = () =>
  useMutation({
    mutationFn: updateTemplate,
    mutationKey: [TemplateKeys.updateTemplate],
  });

export const useGetTemplateQuery = (templateId: string) =>
  useSafeQuery(
    [TemplateKeys.getTemplate, templateId],
    async () => {
      const res = await getTemplate(templateId);
      return res?.data?.data?.template;
    },
    {
      enabled: !!templateId,
      retry: false,
      onError: (err) => {
        const error = err as AxiosResponse;

        if (error.status === 404) {
          window.location.href = '/404';
        }
      },
    },
  );

export const useGetTemplateRequestsQuery = (
  templateId: string,
  sortBy = 'order',
  sortOrder = 'asc',
) =>
  useSafeQuery(
    [TemplateKeys.getTemplateRequests, templateId, sortBy, sortOrder],
    async () => {
      const res = await getTemplateRequests({ templateId, sortBy, sortOrder });
      return res?.data?.data?.requests;
    },
    {
      enabled: !!templateId,
    },
  );

export const useCreateTemplateRequestMutation = () =>
  useMutation({
    mutationFn: createTemplateRequest,
    mutationKey: [TemplateKeys.createTemplateRequest],
  });

export const useUpdateTemplateRequestMutation = () =>
  useMutation({
    mutationFn: updateTemplateRequest,
    mutationKey: [TemplateKeys.updateTemplateRequest],
  });

export const useDeleteTemplateRequestMutation = () =>
  useMutation({
    mutationFn: deleteTemplateRequest,
    mutationKey: [TemplateKeys.deleteTemplateRequest],
  });

export const useGetTemplateRequestGroupsQuery = (templateId: string) =>
  useSafeQuery(
    [TemplateKeys.getTemplateRequestGroups, templateId],
    async () => {
      const { data } = await getTemplateRequestGroups({ templateId });
      if (data && has(data, 'data') && has(data.data, 'request_groups')) {
        return data.data.request_groups as TInviteRequestGroup[];
      }
      return [];
    },
    {
      enabled: !!templateId,
    },
  );

//updateTemplateRequestGroupName
export const useUpdateTemplateRequestGroupNameMutation = () => {
  return useMutation({
    mutationFn: updateTemplateRequestGroupName,
    onSuccess: async () => {
      await queryClient.invalidateQueries([
        TemplateKeys.getTemplateRequestGroups,
      ]);
    },
  });
};

//duplicateTemplateGroupRequest
export const useDuplicateTemplateGroupRequestMutation = () => {
  return useMutation({
    mutationFn: duplicateTemplateGroupRequest,
    onSuccess: async () => {
      await queryClient.invalidateQueries([
        TemplateKeys.getTemplateRequestGroups,
      ]);
    },
  });
};
